import { Focusable } from 'react-aria-components';
import { Tooltip } from '../../display/Tooltip';
import { Spinner } from '../../display/Spinner';

import styles from './ToggleSwitch.module.scss';

type Props = {
  /**
   * Optional custom `aria-label` for the component. By default
   * `aria-label` will be computed based on the `checked` and
   * `indeterminate` props on this component and their corresponding
   * `*Text` props. However because that causes the `aria-label` to
   * change as `ToggleSwitch` is un/checked, that can be confusing
   * for screenreader users in some scenarios. In that case, use this
   * `ariaLabel` prop to clear up any ambiguity.
   */
  'aria-label'?: string;
  /**
   * Indicates whether the switch is checked on or off.
   */
  checked?: boolean;
  /**
   * Indicates whether the switch is disabled.
   */
  disabled?: boolean;
  /**
   * HTML `id` of the input.
   */
  id?: string;
  /**
   * Whether the component should display a loading state.
   */
  isLoading?: boolean;
  /**
   * HTML name attribute, typically used for the key that will be sent to the API.
   */
  name?: string;
  /**
   * Whether to show the "Disabled" tooltip when toggle is disabled.
   */
  showDisabledTooltip?: boolean;
  /**
   * Handler invoked when the switch changes from checked on or off.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /**
   * HTML value attribute, i.e. the actual value of the input.
   */
  value?: string;
};

const getAriaLabel = (props: Props) => {
  if (props['aria-label']) {
    return props['aria-label'];
  }
  if (props.isLoading) {
    return 'Loading';
  }
  return props.checked ? 'On' : 'Off';
};

const getTooltipContent = (props: Props) =>
  props.disabled && props.showDisabledTooltip ? 'Disabled' : '';

/**
 * `ToggleSwitch` is a stylized checkbox used to indicate whether something is on or off.
 */
export const ToggleSwitch = ({
  'aria-label': ariaLabel = undefined,
  checked = undefined,
  disabled = undefined,
  id = undefined,
  isLoading = false,
  name = undefined,
  onChange = () => {},
  showDisabledTooltip = true,
  value = undefined,
}: Props) => {
  const props = {
    'aria-label': ariaLabel,
    checked,
    disabled,
    id,
    isLoading,
    name,
    onChange,
    showDisabledTooltip,
    value,
  } satisfies Props;

  const tooltipContent = getTooltipContent(props);

  const label = (
    <label
      className={styles.root}
      data-checked={props.checked || undefined}
      data-disabled={props.disabled || undefined}
      data-loading={props.isLoading || undefined}
      data-zds
    >
      <span className={styles.innerWrap} data-zds>
        <input
          aria-disabled={props.disabled || props.isLoading}
          aria-label={getAriaLabel(props)}
          checked={props.checked}
          className={styles.input}
          disabled={props.disabled || props.isLoading}
          data-zds
          id={props.id}
          name={props.name}
          onChange={props.onChange}
          type="checkbox"
          value={props.value}
        />
        {props.isLoading && (
          <span className={styles.spinner} data-zds>
            <Spinner />
          </span>
        )}
        {!props.isLoading && (
          <span
            aria-hidden="true"
            className={styles.dot}
            role="presentation"
            data-zds
          />
        )}
      </span>
    </label>
  );

  if (tooltipContent) {
    return (
      <Tooltip content={tooltipContent}>
        <Focusable>{label}</Focusable>
      </Tooltip>
    );
  }

  return label;
};
